/**
 * Event Criteria Panel Component
 * Allows setting display and join criteria for events
 * Based on the adventure CriteriaPanel but simplified for events
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { calendarEventsApi } from '@api/endpoints/calendarEvents'
import { settingsApi } from '@api/endpoints/settings'
import { MeasurementUnit } from '@/types/settings'
import { ConditionConfigPanel } from '@/features/adventures/components/workflow-designer/conditions'
import {
  WorkflowConditionType,
  getConditionTypeLabel,
} from '@/features/adventures/types/workflow.types'
import {
  getCriteriaTypeIcon,
  CRITERIA_CATEGORIES,
  getOperatorDisplay,
  isSubscriptionRequired,
  type CriteriaItem,
  type CriteriaLogicGroup,
} from '@/features/adventures/types/adventure-editor.types'

// Available criteria types for events (subset of adventure criteria)
const EVENT_AVAILABLE_CRITERIA_TYPES: WorkflowConditionType[] = [
  // Weather
  WorkflowConditionType.WeatherTemperature,
  WorkflowConditionType.WeatherCondition,
  // Location
  WorkflowConditionType.Geofence,
  // Time
  WorkflowConditionType.TimeSchedule,
  // Weather day/night (the backend covers day-of-week as part of TimeSchedule)
  WorkflowConditionType.WeatherDayNight,
  // Group
  WorkflowConditionType.GroupMembership,
]

interface EventCriteriaPanelProps {
  type: 'display' | 'join'
  eventId: string
  title: string
  description: string
  icon: React.ReactNode
}

export function EventCriteriaPanel({
  type,
  eventId,
  title,
  description,
  icon,
}: EventCriteriaPanelProps) {
  // Fetch user's measurement unit preference
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.Imperial)

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const prefs = await settingsApi.getUserPreferences()
        setMeasurementUnit(prefs.measurementUnit ?? MeasurementUnit.Imperial)
      } catch (error) {
        console.error('Error fetching user preferences:', error)
      }
    }
    fetchPreferences()
  }, [])

  // Local state for criteria
  const [items, setItems] = useState<CriteriaItem[]>([])
  const [logicGroups, setLogicGroups] = useState<CriteriaLogicGroup[]>([])

  // UI state
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)
  const [addMenuGroupId, setAddMenuGroupId] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track if initial group creation has been attempted
  const hasAttemptedInitialGroupRef = useRef(false)
  const hasFetchedRef = useRef(false)

  // Fetch criteria from API on mount
  useEffect(() => {
    const fetchCriteria = async () => {
      if (!eventId || hasFetchedRef.current) {
        setIsLoading(false)
        return
      }

      hasFetchedRef.current = true

      try {
        const apiCall = type === 'display'
          ? calendarEventsApi.getDisplayCriteria
          : calendarEventsApi.getJoinCriteria

        const groups = await apiCall(eventId)

        // Transform nested response to flat items + groups
        const criteriaItems: CriteriaItem[] = []
        const criteriaLogicGroups: CriteriaLogicGroup[] = []

        for (const group of groups) {
          criteriaLogicGroups.push({
            id: group.id,
            isAnd: group.isAnd,
            orderIndex: group.orderIndex,
          })

          for (const criterion of group.criteria) {
            criteriaItems.push({
              id: criterion.id,
              conditionType: criterion.conditionType as WorkflowConditionType,
              config: criterion.config ?? {},
              logicGroupId: group.id,
              order: criterion.priority ?? 0,
            })
          }
        }

        setItems(criteriaItems)
        setLogicGroups(criteriaLogicGroups)
      } catch (err) {
        console.error(`[EventCriteriaPanel] Failed to fetch ${type} criteria:`, err)
        setError(`Failed to load ${type} criteria`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCriteria()
  }, [eventId, type])

  // Auto-create a logic group if none exist
  useEffect(() => {
    const createInitialGroup = async () => {
      if (!isLoading && logicGroups.length === 0 && !hasAttemptedInitialGroupRef.current && eventId) {
        hasAttemptedInitialGroupRef.current = true
        try {
          await handleAddLogicGroup()
        } catch (err) {
          console.error('Failed to create initial group:', err)
        }
      }
    }
    createInitialGroup()
  }, [isLoading, logicGroups.length, eventId])

  // Get items for a specific group
  const getItemsForGroup = useCallback((groupId: string) => {
    return items
      .filter((item) => item.logicGroupId === groupId)
      .sort((a, b) => a.order - b.order)
  }, [items])

  // Toggle item expansion
  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  // Handle add menu open
  const handleAddMenuOpen = (event: React.MouseEvent<HTMLElement>, groupId?: string) => {
    setAddMenuAnchor(event.currentTarget)
    setAddMenuGroupId(groupId)
  }

  // Handle add menu close
  const handleAddMenuClose = () => {
    setAddMenuAnchor(null)
    setAddMenuGroupId(undefined)
  }

  // Handle add criteria
  const handleAddCriteria = async (conditionType: WorkflowConditionType) => {
    if (!addMenuGroupId || !eventId) return
    handleAddMenuClose()

    setIsSaving(true)
    setError(null)

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.addDisplayCriterion
        : calendarEventsApi.addJoinCriterion

      const result = await apiCall(eventId, {
        conditionType,
        config: {},
        logicGroupId: addMenuGroupId,
        order: items.filter((i) => i.logicGroupId === addMenuGroupId).length,
      })

      // Add to local state
      const newItem: CriteriaItem = {
        id: result.id,
        conditionType: result.conditionType as WorkflowConditionType,
        config: result.config ?? {},
        logicGroupId: addMenuGroupId,
        order: result.priority ?? 0,
      }

      setItems((prev) => [...prev, newItem])
      setExpandedItems((prev) => new Set([...prev, result.id]))
    } catch (err) {
      console.error('Failed to add criterion:', err)
      setError('Failed to add criterion')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle update criteria
  const handleUpdateCriteria = async (itemId: string, config: Record<string, unknown>) => {
    if (!eventId) return

    // Update local state immediately for responsiveness
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, config } : item
      )
    )

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.updateDisplayCriterion
        : calendarEventsApi.updateJoinCriterion

      await apiCall(eventId, itemId, { config })
    } catch (err) {
      console.error('Failed to update criterion:', err)
      setError('Failed to save changes')
    }
  }

  // Handle remove criteria
  const handleRemoveCriteria = async (itemId: string) => {
    if (!eventId) return

    setIsSaving(true)
    setError(null)

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.deleteDisplayCriterion
        : calendarEventsApi.deleteJoinCriterion

      await apiCall(eventId, itemId)

      // Remove from local state
      setItems((prev) => prev.filter((item) => item.id !== itemId))
    } catch (err) {
      console.error('Failed to remove criterion:', err)
      setError('Failed to remove criterion')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle add logic group
  const handleAddLogicGroup = async () => {
    if (!eventId) return

    setIsSaving(true)
    setError(null)

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.createDisplayCriteriaGroup
        : calendarEventsApi.createJoinCriteriaGroup

      const result = await apiCall(eventId, { isAnd: true })

      // Add to local state
      const newGroup: CriteriaLogicGroup = {
        id: result.id,
        isAnd: result.isAnd,
        orderIndex: result.orderIndex,
      }

      setLogicGroups((prev) => [...prev, newGroup])
    } catch (err) {
      console.error('Failed to add logic group:', err)
      setError('Failed to add logic group')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle update logic group
  const handleUpdateLogicGroup = async (groupId: string, operator: 'AND' | 'OR') => {
    if (!eventId) return

    const isAnd = operator === 'AND'

    // Update local state immediately
    setLogicGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, isAnd } : group
      )
    )

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.updateDisplayCriteriaGroup
        : calendarEventsApi.updateJoinCriteriaGroup

      await apiCall(eventId, groupId, { isAnd })
    } catch (err) {
      console.error('Failed to update logic group:', err)
      setError('Failed to save changes')
    }
  }

  // Handle remove logic group
  const handleRemoveLogicGroup = async (groupId: string) => {
    if (!eventId || logicGroups.length <= 1) return

    setIsSaving(true)
    setError(null)

    try {
      const apiCall = type === 'display'
        ? calendarEventsApi.deleteDisplayCriteriaGroup
        : calendarEventsApi.deleteJoinCriteriaGroup

      await apiCall(eventId, groupId)

      // Remove from local state
      setLogicGroups((prev) => prev.filter((group) => group.id !== groupId))
      setItems((prev) => prev.filter((item) => item.logicGroupId !== groupId))
    } catch (err) {
      console.error('Failed to remove logic group:', err)
      setError('Failed to remove logic group')
    } finally {
      setIsSaving(false)
    }
  }

  // Filter categories to only show available types for events
  const filteredCategories = useMemo(() => {
    return CRITERIA_CATEGORIES
      .filter((category) => category.types.some((t) => EVENT_AVAILABLE_CRITERIA_TYPES.includes(t)))
      .map((category) => ({
        ...category,
        types: category.types.filter((t) => EVENT_AVAILABLE_CRITERIA_TYPES.includes(t)),
      }))
  }, [])

  // Render a single criteria item
  const renderCriteriaItem = (item: CriteriaItem) => {
    const isExpanded = expandedItems.has(item.id)
    const typeLabel = getConditionTypeLabel(item.conditionType)
    const typeIcon = getCriteriaTypeIcon(item.conditionType)

    return (
      <Paper
        key={item.id}
        variant="outlined"
        sx={{ mb: 1, overflow: 'hidden' }}
      >
        {/* Item Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => toggleItemExpanded(item.id)}
        >
          <Typography sx={{ fontSize: '1.2rem' }}>{typeIcon}</Typography>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
            {typeLabel}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleRemoveCriteria(item.id)
            }}
            sx={{ color: 'error.main' }}
            disabled={isSaving}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        {/* Item Config */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
            <ConditionConfigPanel
              conditionType={item.conditionType}
              config={item.config}
              onChange={(config) => handleUpdateCriteria(item.id, config)}
              measurementUnit={measurementUnit}
            />
          </Box>
        </Collapse>
      </Paper>
    )
  }

  // Render a logic group with its items
  const renderLogicGroup = (group: CriteriaLogicGroup, index: number) => {
    const groupItems = getItemsForGroup(group.id)
    const operator = getOperatorDisplay(group.isAnd)

    return (
      <Paper
        key={group.id}
        variant="outlined"
        sx={{ mb: 2, p: 2, bgcolor: 'background.default' }}
      >
        {/* OR indicator between groups */}
        {index > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: -3 }}>
            <Chip
              label="OR"
              size="small"
              sx={{
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                fontWeight: 'bold',
                fontSize: '0.75rem',
              }}
            />
          </Box>
        )}

        {/* Group Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Logic Group
            </Typography>
            <ToggleButtonGroup
              value={operator}
              exclusive
              onChange={(_, newOperator) => {
                if (newOperator) {
                  handleUpdateLogicGroup(group.id, newOperator)
                }
              }}
              size="small"
            >
              <ToggleButton value="AND" sx={{ px: 2 }}>
                <Tooltip title="All criteria in this group must be met">
                  <span>AND</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="OR" sx={{ px: 2 }}>
                <Tooltip title="Any criteria in this group can be met">
                  <span>OR</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {logicGroups.length > 1 && (
            <Tooltip title="Delete group and all criteria in it">
              <IconButton
                size="small"
                onClick={() => handleRemoveLogicGroup(group.id)}
                color="error"
                disabled={isSaving}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Group items */}
        <Box>
          {groupItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
              No criteria in this group
            </Typography>
          ) : (
            groupItems.map(renderCriteriaItem)
          )}
        </Box>

        {/* Add to group button */}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => handleAddMenuOpen(e, group.id)}
          sx={{ mt: 1 }}
          disabled={isSaving}
        >
          Add Criteria to Group
        </Button>
      </Paper>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Info about how criteria work */}
      {!isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>How criteria work:</strong> Items within a group are combined using the group's operator (AND/OR).
            If you have multiple groups, <strong>any group passing</strong> means the overall criteria passes.
          </Typography>
        </Alert>
      )}

      {/* Logic groups */}
      {!isLoading && logicGroups
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((group, index) => renderLogicGroup(group, index))}

      {/* Action buttons */}
      {!isLoading && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddLogicGroup}
            disabled={isSaving}
          >
            Add Logic Group
          </Button>
        </Box>
      )}

      {/* Add criteria menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        PaperProps={{
          sx: { maxHeight: 400, width: 280 },
        }}
      >
        {filteredCategories.map((category, categoryIndex) => (
          <Box key={category.name}>
            {categoryIndex > 0 && <Divider />}
            <MenuItem disabled sx={{ opacity: 1 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Typography>{category.icon}</Typography>
              </ListItemIcon>
              <ListItemText
                primary={category.name}
                primaryTypographyProps={{ fontWeight: 'bold', variant: 'body2' }}
              />
            </MenuItem>
            {category.types.map((conditionType) => (
              <MenuItem
                key={conditionType}
                onClick={() => handleAddCriteria(conditionType)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Typography>{getCriteriaTypeIcon(conditionType)}</Typography>
                </ListItemIcon>
                <ListItemText
                  primary={getConditionTypeLabel(conditionType)}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
                <Chip
                  label={isSubscriptionRequired(conditionType) ? 'Pro' : 'Free'}
                  size="small"
                  color={isSubscriptionRequired(conditionType) ? 'warning' : 'success'}
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>
    </Box>
  )
}

export default EventCriteriaPanel
